import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, ChevronDown, ChevronRight, FolderTree, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerCategoriesList, useCustomerCategoryMutations } from '~/hooks/use-customer-categories';
import {
	buildCustomerCategoryTree,
	tokenToForm,
	type CustomerCategoryFormValues,
	type CustomerCategoryTreeNode
} from '~/lib/schemas/customer-category';
import { CustomerCategoryEditDialog } from './category-edit-dialog';

const Bool = ({ on, title }: { on?: boolean; title: string }) =>
	on ? (
		<span className="text-success-fg" title={title}>
			✓
		</span>
	) : (
		<span className="text-foreground-disabled">—</span>
	);

function collectMatchIds(roots: CustomerCategoryTreeNode[], query: string): { matches: Set<number>; expand: Set<number> } {
	const matches = new Set<number>();
	const expand = new Set<number>();
	const walk = (node: CustomerCategoryTreeNode, ancestors: number[]): boolean => {
		const selfMatch = (node.nm ?? '').toLowerCase().includes(query) || (node.cd ?? '').toLowerCase().includes(query);
		let childMatch = false;
		for (const child of node.children) childMatch = walk(child, [...ancestors, node.id!]) || childMatch;
		if (selfMatch || childMatch) {
			if (node.id != null) matches.add(node.id);
			if (childMatch) for (const a of ancestors.concat(node.id!)) expand.add(a);
		}
		return selfMatch || childMatch;
	};
	for (const r of roots) walk(r, []);
	return { matches, expand };
}

function flattenVisible(roots: CustomerCategoryTreeNode[], expanded: Set<number>, matches: Set<number> | null): CustomerCategoryTreeNode[] {
	const out: CustomerCategoryTreeNode[] = [];
	const walk = (nodes: CustomerCategoryTreeNode[]) => {
		for (const node of nodes) {
			if (matches && node.id != null && !matches.has(node.id)) continue;
			out.push(node);
			if (node.children.length && expanded.has(node.id!)) walk(node.children);
		}
	};
	walk(roots);
	return out;
}

/** Customer categories (Κατηγορίες Πελατών) — hierarchical tree with parity to the legacy admin tree-list. */
export function CustomerCategoriesView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCustomerCategoriesList();
	const { remove } = useCustomerCategoryMutations();

	const [search, setSearch] = useState('');
	const [expanded, setExpanded] = useState<Set<number>>(new Set());
	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<CustomerCategoryFormValues | null>(null);
	const [deleteNode, setDeleteNode] = useState<CustomerCategoryTreeNode | null>(null);

	const all = data ?? [];
	const roots = useMemo(() => buildCustomerCategoryTree(all), [all]);
	const query = search.trim().toLowerCase();
	const { matches, autoExpand } = useMemo(() => {
		if (!query) return { matches: null as Set<number> | null, autoExpand: null as Set<number> | null };
		const res = collectMatchIds(roots, query);
		return { matches: res.matches, autoExpand: res.expand };
	}, [roots, query]);

	const effectiveExpanded = query && autoExpand ? autoExpand : expanded;
	const rows = useMemo(() => flattenVisible(roots, effectiveExpanded, matches), [roots, effectiveExpanded, matches]);

	const toggle = (id: number) =>
		setExpanded(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const confirmDelete = () => {
		if (!deleteNode?.id) return;
		remove.mutate(deleteNode.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteNode(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	const total = all.length;
	const hasChildren = (deleteNode?.children.length ?? 0) > 0;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('customers.categories.title')}</h1>
					<p className="mt-1 text-sm text-foreground-muted">{t('customers.categories.subtitle', { count: total })}</p>
				</div>
				<Button
					onClick={() => {
						setEditing(null);
						setEditOpen(true);
					}}>
					<Plus size={16} /> {t('customers.categories.add')}
				</Button>
			</div>

			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search')} className="pl-8" />
				</div>
			</div>

			<div className="overflow-x-auto rounded-md border border-border bg-surface">
				{isError ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
						<Button variant="outline" onClick={() => void refetch()}>
							{t('common.retry')}
						</Button>
					</div>
				) : isLoading ? (
					<div className="flex flex-col gap-2 p-3">
						{Array.from({ length: 8 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<FolderTree size={36} className="text-foreground-disabled" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<table className="w-full border-collapse text-sm">
						<thead className="bg-elevated text-xs text-foreground-muted">
							<tr className="border-b border-border">
								<th className="px-3 py-2 text-left font-medium" style={{ minWidth: 280 }}>
									{t('customers.categories.columns.name')}
								</th>
								<th className="px-3 py-2 text-left font-medium whitespace-nowrap" style={{ width: '160px' }}>
									{t('customers.categories.columns.code')}
								</th>
								<th className="px-3 py-2 text-right font-medium whitespace-nowrap" style={{ width: '110px' }}>
									{t('customers.categories.columns.discount')}
								</th>
								<th className="px-3 py-2 text-center font-medium whitespace-nowrap" style={{ width: '80px' }}>
									{t('customers.categories.columns.visible')}
								</th>
								<th className="px-3 py-2 text-right font-medium whitespace-nowrap" style={{ width: '70px' }}>
									{t('customers.categories.columns.sort')}
								</th>
								<th className="px-2 py-2" style={{ width: 88 }} />
							</tr>
						</thead>
						<tbody>
							{rows.map(node => {
								const expandable = node.children.length > 0;
								const isOpen = effectiveExpanded.has(node.id!);
								return (
									<tr key={node.id} className="group border-b border-border/60 hover:bg-muted/50">
										<td className="px-3 py-2" style={{ minWidth: 280 }}>
											<div className="flex items-center gap-1.5" style={{ paddingLeft: node.depth * 20 }}>
												{expandable ? (
													<button
														type="button"
														onClick={() => toggle(node.id!)}
														className="rounded-sm p-0.5 text-foreground-subtle hover:bg-muted hover:text-foreground"
														aria-label={isOpen ? t('common.collapse') : t('common.expand')}>
														{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
													</button>
												) : (
													<span className="w-[22px]" />
												)}
												<span className="truncate text-sm font-medium text-foreground">{node.nm}</span>
											</div>
										</td>
										<td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-foreground-muted">{node.cd || '—'}</td>
										<td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-foreground-muted">
											{node.dsc != null ? node.dsc : '—'}
										</td>
										<td className="px-3 py-2 text-center whitespace-nowrap">
											<Bool on={node.iv} title={t('customers.categories.columns.visible')} />
										</td>
										<td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-foreground-muted">{node.si ?? 0}</td>
										<td className="px-2 py-1 text-center whitespace-nowrap">
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												aria-label={t('common.edit')}
												onClick={() => {
													setEditing(tokenToForm(node));
													setEditOpen(true);
												}}>
												<Pencil size={14} />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7 text-danger-fg"
												aria-label={t('common.delete')}
												onClick={() => setDeleteNode(node)}>
												<Trash2 size={14} />
											</Button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}
			</div>

			<CustomerCategoryEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} all={all} />

			<Dialog open={Boolean(deleteNode)} onOpenChange={open => !open && setDeleteNode(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('customers.categories.deleteTitle')}</DialogTitle>
						<DialogDescription>
							{hasChildren
								? t('customers.categories.deleteWithChildrenBody', { name: deleteNode?.nm ?? '' })
								: t('customers.categories.deleteBody', { name: deleteNode?.nm ?? '' })}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteNode(null)} disabled={remove.isPending}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
